// Admin "Customer" detail — clicking a row on CustomersPage.jsx opens this:
// the organization's full request + quotation history and its contact
// people (self-registered buyer accounts tied to this org), plus edit/
// tier-change/deactivate/reactivate/delete actions (customers/products/
// users multi-select bulk-actions task, section 1's explicit brief).
//
// Read-only history — no action lives on a request/quotation row here;
// clicking one opens the SAME detail screens AllRequestsPage/
// AllProformasPage already link to, so this never duplicates that logic.

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext.jsx';
import {
  useAdminCustomerDetail,
  useUpdateCustomer,
  useDeactivateCustomer,
  useReactivateCustomer,
  useDeleteCustomer,
} from '../hooks/useAdminCustomers.js';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import { statusLabel } from '../i18n.js';
import { formatDate, formatDateTime } from '../utils/formatDate.js';
import { formatMoney } from 'shared/money.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import styles from './CustomerDetailPage.module.css';

const TIERS = ['government', 'ngo', 'corporate', 'retail'];

function EditForm({ organization, onSubmit, onCancel, isPending }) {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: organization.name || '',
    nameAm: organization.name_am || '',
    tin: organization.tin || '',
    tier: organization.tier,
    contactName: organization.contact_name || '',
    contactEmail: organization.contact_email || '',
    contactPhone: organization.contact_phone || '',
    address: organization.address || '',
  });
  const [clientErrorId, setClientErrorId] = useState(null);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setClientErrorId(null);
    if (!form.name.trim()) {
      setClientErrorId('customers.form.nameRequired');
      return;
    }
    onSubmit({
      name: form.name.trim(),
      nameAm: form.nameAm.trim() || undefined,
      tin: form.tin.trim() || undefined,
      tier: form.tier,
      contactName: form.contactName.trim(),
      contactEmail: form.contactEmail.trim() || undefined,
      contactPhone: form.contactPhone.trim() || undefined,
      address: form.address.trim() || undefined,
    });
  };

  return (
    <form className={styles.editForm} onSubmit={handleSubmit}>
      <label className={styles.field}>
        {t('customers.form.nameLabel')}
        <input type="text" value={form.name} onChange={set('name')} />
      </label>
      <label className={styles.field}>
        {t('customers.form.nameAmLabel')}
        <input type="text" value={form.nameAm} onChange={set('nameAm')} />
      </label>
      <label className={styles.field}>
        {t('customers.form.tinLabel')}
        <input type="text" value={form.tin} onChange={set('tin')} />
      </label>
      <label className={styles.field}>
        {t('customers.form.tierLabel')}
        <select value={form.tier} onChange={set('tier')}>
          {TIERS.map((tier) => (
            <option key={tier} value={tier}>
              {t(`customers.tier.${tier}`)}
            </option>
          ))}
        </select>
      </label>
      <label className={styles.field}>
        {t('customers.form.contactNameLabel')}
        <input type="text" value={form.contactName} onChange={set('contactName')} />
      </label>
      <label className={styles.field}>
        {t('customers.form.contactEmailLabel')}
        <input type="email" value={form.contactEmail} onChange={set('contactEmail')} />
      </label>
      <label className={styles.field}>
        {t('customers.form.contactPhoneLabel')}
        <input type="text" value={form.contactPhone} onChange={set('contactPhone')} />
      </label>
      <label className={styles.field}>
        {t('customers.form.addressLabel')}
        <input type="text" value={form.address} onChange={set('address')} />
      </label>
      <div className={styles.formActions}>
        <button type="submit" className={styles.primaryButton} disabled={isPending}>
          {isPending ? t('customers.form.submitting') : t('customers.form.submit')}
        </button>
        <button type="button" className={styles.linkButton} onClick={onCancel}>
          {t('customers.form.cancel')}
        </button>
        {clientErrorId && <span className={styles.formError}>{t(clientErrorId)}</span>}
      </div>
    </form>
  );
}

export default function CustomerDetailPage() {
  const { id } = useParams();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const { confirm, toastOk, toastErr } = useActionFeedback();
  const [editing, setEditing] = useState(false);

  const { data, isLoading, isError, error, refetch } = useAdminCustomerDetail(id);
  const updateMutation = useUpdateCustomer();
  const deactivateMutation = useDeactivateCustomer();
  const reactivateMutation = useReactivateCustomer();
  const deleteMutation = useDeleteCustomer();

  if (isLoading) return <LoadingState variant="page" />;
  if (isError || !data) {
    return <ErrorState messageId="customers.errorBody" error={error} onRetry={refetch} />;
  }

  const { organization, requests, quotations, contacts } = data;
  const displayName =
    lang === 'am' && organization.name_am ? organization.name_am : organization.name;

  const handleUpdate = (body) => {
    updateMutation.mutate(
      { id: organization.id, body },
      {
        onSuccess: () => {
          setEditing(false);
          toastOk(t('customers.updatedToast'));
        },
        onError: (err) => toastErr(err, ['ORGANIZATION_NOT_FOUND', 'VALIDATION_ERROR']),
      }
    );
  };

  const handleDeactivate = async () => {
    const ok = await confirm({
      title: t('customers.deactivateConfirmTitle', { name: displayName }),
      message: t('customers.deactivateConfirmBody'),
      confirmLabel: t('customers.detail.deactivateButton'),
      tone: 'danger',
    });
    if (!ok) return;
    deactivateMutation.mutate(organization.id, {
      onSuccess: () => toastOk(t('customers.deactivatedToast')),
      onError: (err) => toastErr(err, ['ORGANIZATION_NOT_FOUND', 'ORGANIZATION_ALREADY_INACTIVE']),
    });
  };

  const handleReactivate = () => {
    reactivateMutation.mutate(organization.id, {
      onSuccess: () => toastOk(t('customers.reactivatedToast')),
      onError: (err) => toastErr(err, ['ORGANIZATION_NOT_FOUND', 'ORGANIZATION_ALREADY_ACTIVE']),
    });
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: t('customers.deleteConfirmTitle', { name: displayName }),
      message: t(
        organization.request_count > 0 || organization.quotation_count > 0
          ? 'customers.deleteConfirmBodyWillDeactivate'
          : 'customers.deleteConfirmBodyWillDelete'
      ),
      confirmLabel: t('customers.detail.deleteButton'),
      tone: 'danger',
    });
    if (!ok) return;
    deleteMutation.mutate(organization.id, {
      onSuccess: (result) => {
        toastOk(
          t(result.outcome === 'deleted' ? 'customers.deletedToast' : 'customers.deactivatedToast')
        );
        if (result.outcome === 'deleted') navigate('/customers');
      },
      onError: (err) => toastErr(err, ['ORGANIZATION_NOT_FOUND']),
    });
  };

  return (
    <div className={styles.page}>
      <Link to="/customers" className={styles.backLink}>
        {t('customers.backToList')}
      </Link>

      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>{displayName}</h1>
          <span
            className={
              organization.status === 'suspended' ? styles.statusInactive : styles.statusActive
            }
          >
            {t(`customers.status.${organization.status}`)}
          </span>
        </div>
        <div className={styles.headerActions}>
          <button type="button" className={styles.linkButton} onClick={() => setEditing((p) => !p)}>
            {t('customers.detail.editButton')}
          </button>
          {organization.status === 'suspended' ? (
            <button
              type="button"
              className={styles.linkButton}
              onClick={handleReactivate}
              disabled={reactivateMutation.isPending}
            >
              {t('customers.detail.reactivateButton')}
            </button>
          ) : (
            <button
              type="button"
              className={styles.deactivateLink}
              onClick={handleDeactivate}
              disabled={deactivateMutation.isPending}
            >
              {t('customers.detail.deactivateButton')}
            </button>
          )}
          <button
            type="button"
            className={styles.deleteLink}
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            {t('customers.detail.deleteButton')}
          </button>
        </div>
      </div>

      {editing && (
        <EditForm
          organization={organization}
          onSubmit={handleUpdate}
          onCancel={() => setEditing(false)}
          isPending={updateMutation.isPending}
        />
      )}

      <dl className={styles.infoGrid}>
        <dt>{t('customers.col.tin')}</dt>
        <dd className="tabularNums">{organization.tin || '—'}</dd>
        <dt>{t('customers.col.tier')}</dt>
        <dd>{t(`customers.tier.${organization.tier}`)}</dd>
        <dt>{t('customers.col.contact')}</dt>
        <dd>{organization.contact_name}</dd>
        <dt>{t('customers.col.email')}</dt>
        <dd>{organization.contact_email || '—'}</dd>
        <dt>{t('customers.col.phone')}</dt>
        <dd>{organization.contact_phone || '—'}</dd>
        <dt>{t('customers.col.valueWon')}</dt>
        <dd className="tabularNums">{formatMoney(organization.total_value_won)}</dd>
      </dl>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('customers.detail.contactsTitle')}</h2>
        {contacts.length === 0 ? (
          <p className={styles.emptyNote}>{t('customers.detail.contactsEmpty')}</p>
        ) : (
          <table className={styles.table}>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id}>
                  <td>{c.full_name}</td>
                  <td>{c.email}</td>
                  <td>{c.phone || '—'}</td>
                  <td className="tabularNums">
                    {formatDateTime(c.last_login_at, lang) || t('users.neverLoggedIn')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('customers.detail.requestsTitle')}</h2>
        {requests.length === 0 ? (
          <p className={styles.emptyNote}>{t('customers.detail.requestsEmpty')}</p>
        ) : (
          <table className={styles.table}>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id}>
                  <td>
                    <Link to={`/all-requests/${r.id}`} className={styles.nameLink}>
                      {r.ref_no}
                    </Link>
                  </td>
                  <td>{statusLabel(r.status, lang)}</td>
                  <td className="tabularNums">{formatDate(r.created_at, lang)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('customers.detail.quotationsTitle')}</h2>
        {quotations.length === 0 ? (
          <p className={styles.emptyNote}>{t('customers.detail.quotationsEmpty')}</p>
        ) : (
          <table className={styles.table}>
            <tbody>
              {quotations.map((q) => (
                <tr key={q.id}>
                  <td>
                    {q.proforma_no ? (
                      <Link to={`/all-proformas/${q.id}`} className={styles.nameLink}>
                        {q.proforma_no}
                        {q.revision > 1 ? `-R${q.revision}` : ''}
                      </Link>
                    ) : (
                      // A draft/rejected quotation has no proforma_no yet
                      // (allocated only on approval — root CLAUDE.md rule
                      // #4) — link its parent request instead of a dead
                      // /all-proformas/:id.
                      <Link to={`/all-requests/${q.request_id}`} className={styles.nameLink}>
                        {q.request_ref_no}
                      </Link>
                    )}
                  </td>
                  <td>{statusLabel(q.status, lang)}</td>
                  <td className="tabularNums">
                    {formatMoney(q.total)} {q.currency}
                  </td>
                  <td className="tabularNums">{formatDate(q.created_at, lang)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
